import { AlertCircle, Loader2, Plug2 } from 'lucide-react';
import clsx from 'clsx';

import { endpointTypeName } from '@/portainer/filters/filters';
import { stripProtocol } from '@/react/common/string-utils';
import {
  getEnvironmentTypeIcon,
  isD2KSwarm,
  isEdgeEnvironment,
  isUnassociatedEdgeEnvironment,
} from '@/react/portainer/environments/utils';
import {
  Environment,
  EnvironmentId,
  EnvironmentStatus,
} from '@/react/portainer/environments/types';
import {
  ENVIRONMENTS_POLLING_INTERVAL,
  useEnvironmentList,
} from '@/react/portainer/environments/queries/useEnvironmentList';

import { EdgeIndicator } from '@@/EdgeIndicator';
import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { Icon } from '@@/Icon';

import styles from './WizardEndpointsList.module.css';

interface Props {
  environmentIds: EnvironmentId[];
}

export function WizardEndpointsList({ environmentIds }: Props) {
  const { environments } = useEnvironmentList(
    { endpointIds: environmentIds, pageLimit: 0 },
    {
      refetchInterval: (environments) => {
        if (!environments) {
          return false;
        }

        if (
          !environments.value.some(
            (environment) =>
              isUnassociatedEdgeEnvironment(environment) ||
              environment.Status === EnvironmentStatus.Provisioning
          )
        ) {
          return false;
        }

        return ENVIRONMENTS_POLLING_INTERVAL;
      },

      enabled: environmentIds.length > 0,
    }
  );

  return (
    <Widget>
      <WidgetTitle icon={Plug2} title="New Environments" />
      <WidgetBody>
        {environments.map((environment) => (
          <div className={styles.wizardListWrapper} key={environment.Id}>
            <div
              className={clsx(
                styles.wizardListImage,
                'text-5xl text-blue-8 th-highcontrast:text-white th-dark:text-blue-7'
              )}
            >
              <Icon
                icon={getEnvironmentTypeIcon(
                  environment.Type,
                  environment.ContainerEngine,
                  isD2KSwarm(environment)
                )}
                className="mr-1"
              />
            </div>
            <div className={styles.wizardListTitle}>{environment.Name}</div>
            <div className={styles.wizardListSubtitle}>
              URL: {stripProtocol(environment.URL)}
            </div>
            <div className={styles.wizardListType}>
              Type: {endpointTypeName(environment.Type)}
            </div>
            <ProvisioningStatus environment={environment} />
            {isEdgeEnvironment(environment.Type) && (
              <div className={styles.wizardListEdgeStatus}>
                <EdgeIndicator environment={environment} />
              </div>
            )}
          </div>
        ))}
      </WidgetBody>
    </Widget>
  );
}

/** What an environment that is still being set up is doing, or why it failed. */
function ProvisioningStatus({ environment }: { environment: Environment }) {
  const isProvisioning = environment.Status === EnvironmentStatus.Provisioning;
  const isError = environment.Status === EnvironmentStatus.Error;
  const summary = environment.StatusMessage?.summary;

  if (!summary || (!isProvisioning && !isError)) {
    return null;
  }

  return (
    <div
      className={clsx(styles.wizardListStatus, 'flex items-center gap-1', {
        'text-muted': isProvisioning,
        'text-danger': isError,
      })}
      title={environment.StatusMessage?.detail}
    >
      <Icon
        icon={isError ? AlertCircle : Loader2}
        className={clsx('flex-none', { 'animate-spin-slow': isProvisioning })}
      />
      {summary}
    </div>
  );
}
